import { useState } from 'react';
import type { ReactNode } from 'react';
import { hojeBrasilia, periodoDo } from '../../paineis';
import type { AtalhoDePeriodo, Periodo } from '../../paineis';
import './dashboard.css';

/**
 * Peças comuns das telas 11 e 12 (Dashboard, Gráficos, Insights): gráfico de barras com a linha
 * da base, sparkline, linha curta, período na URL e o CSV gerado no navegador.
 *
 * Gráfico em SVG puro, como na Central de Vendas — nenhuma biblioteca nova. Nenhuma peça daqui
 * calcula número: desenha o que o servidor mandou.
 */

/* ------------------------------------------------------------------ *
 * Ícones que o Icone.tsx ainda não tem (desenhos do próprio mockup)
 * ------------------------------------------------------------------ */

const DESENHOS_EXTRAS = {
  tv: 'M2.8 4.6h14.4v9.6H2.8zM7 17h6M10 14.2V17',
  chart: 'M3.4 16.6h13.2M6.2 14V9.2M10 14V4.8M13.8 14v-4.2',
  bulb: 'M7.4 13.2a5 5 0 1 1 5.2 0v1.6H7.4ZM8 17h4M10 2.6v.8',
  'up-t': 'M10 15.4V5M6.2 8.8 10 5l3.8 3.8',
  'down-t': 'M10 4.6V15M6.2 11.2 10 15l3.8-3.8',
} as const;
export type IconeExtraNome = keyof typeof DESENHOS_EXTRAS;

export function IconeExtra({
  nome,
  tamanho = 15,
  traco = 1.6,
}: {
  nome: IconeExtraNome;
  tamanho?: number;
  traco?: number;
}) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={traco}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: 'none' }}
      aria-hidden="true"
    >
      <path d={DESENHOS_EXTRAS[nome]} />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * Período e consulta na URL (#/rota?p=7d  ou  ?de=2026-09-01&ate=2026-09-22)
 * ------------------------------------------------------------------ */

const ATALHOS: readonly AtalhoDePeriodo[] = ['hoje', 'ontem', '7d', '30d', 'mes'];
export const ROTULO_DO_ATALHO: Record<AtalhoDePeriodo, string> = {
  hoje: 'Hoje',
  ontem: 'Ontem',
  '7d': '7 dias',
  '30d': '30 dias',
  mes: 'Este mês',
};
const DIA = /^\d{4}-\d{2}-\d{2}$/;

export function periodoDaUrl(
  parametros: URLSearchParams,
  padrao: AtalhoDePeriodo,
): { periodo: Periodo; atalho: AtalhoDePeriodo | 'personalizado' } {
  const de = parametros.get('de');
  const ate = parametros.get('ate');
  if (de && ate && DIA.test(de) && DIA.test(ate) && de <= ate)
    return { periodo: { de, ate }, atalho: 'personalizado' };
  const p = parametros.get('p') as AtalhoDePeriodo | null;
  const atalho = p && ATALHOS.includes(p) ? p : padrao;
  return { periodo: periodoDo(atalho), atalho };
}

/** Troca parâmetros da consulta mantendo a rota atual (#/dashboard ou #/desempenho…). */
export function trocarConsulta(parametros: URLSearchParams, mudar: Record<string, string | null>) {
  const rota = (window.location.hash.split('?')[0] ?? '').replace(/^#/, '') || '/';
  const q = new URLSearchParams(parametros);
  for (const [k, v] of Object.entries(mudar)) {
    if (v === null) q.delete(k);
    else q.set(k, v);
  }
  const s = q.toString();
  window.location.hash = `${rota}${s ? `?${s}` : ''}`;
}

export const diaCurto = (d: string) => d.split('-').reverse().slice(0, 2).join('/');
export const textoDoPeriodo = (p: Periodo) =>
  p.de === p.ate ? diaCurto(p.de) : `${diaCurto(p.de)} a ${diaCurto(p.ate)}`;

/** "14:08" quando é de hoje; "21/09 14:08" quando não é. */
export function quando(iso: string): string {
  const d = new Date(iso);
  const dia = d.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  const hm = d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
  return dia === hojeBrasilia() ? hm : `${diaCurto(dia)} ${hm}`;
}

/** 'R$ 42.860' para valores grandes (como no design); centavos quando o valor é pequeno. */
export const reaisCompacto = (v: number) =>
  `R$ ${v.toLocaleString('pt-BR', {
    minimumFractionDigits: Math.abs(v) >= 1000 ? 0 : 2,
    maximumFractionDigits: Math.abs(v) >= 1000 ? 0 : 2,
  })}`;

/* ------------------------------------------------------------------ *
 * CSV gerado no navegador (nada vai ao servidor)
 * ------------------------------------------------------------------ */

// O escape (inclusive contra fórmula) mora em design/csv.ts — um só para todas as telas.
export { baixarCsv } from '../../design/csv';
export type { Celula } from '../../design/csv';

/* ------------------------------------------------------------------ *
 * Gráfico de barras + linha tracejada da base
 * ------------------------------------------------------------------ */

export interface PontoDasBarras {
  readonly rotulo: string;
  readonly atual: number;
  readonly base: number | null;
  /** Hora que ainda não aconteceu: barra apagada. */
  readonly futura?: boolean;
  /** Cabeçalho da dica (ex.: "HOJE · 16:00–16:59"). */
  readonly dica?: string;
}

export function BarrasComBase({
  pontos,
  formato,
  rotuloAtual,
  rotuloBase,
  cadaRotulo = 1,
  descricao,
}: {
  pontos: readonly PontoDasBarras[];
  formato: (v: number) => string;
  rotuloAtual: string;
  rotuloBase: string | null;
  /** Mostra 1 rótulo do eixo a cada N barras. */
  cadaRotulo?: number;
  descricao: string;
}) {
  const [foco, setFoco] = useState<number | null>(null);
  const L = 1000;
  const A = 200;
  const topo = 10;
  const chao = A - 12;
  const n = Math.max(1, pontos.length);
  const max = Math.max(1, ...pontos.map((p) => Math.max(p.atual, p.base ?? 0)));
  const y = (v: number) => chao - (v / max) * (chao - topo);
  const slot = L / n;
  const larg = Math.min(64, slot * 0.72);
  const cx = (i: number) => slot * i + slot / 2;
  // A linha da base quebra onde a base não existe (não liga pontos por cima de um buraco).
  const trechos: string[] = [];
  let atual: string[] = [];
  pontos.forEach((p, i) => {
    if (p.base === null) {
      if (atual.length > 1) trechos.push(atual.join(' '));
      atual = [];
    } else atual.push(`${cx(i).toFixed(1)},${y(p.base).toFixed(1)}`);
  });
  if (atual.length > 1) trechos.push(atual.join(' '));
  const pf = foco === null ? null : pontos[foco];

  return (
    <div className="db-barras">
      <div className="db-barras-area" onMouseLeave={() => setFoco(null)}>
        <svg
          viewBox={`0 0 ${String(L)} ${String(A)}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={descricao}
        >
          {[0, 0.25, 0.5, 0.75].map((f) => (
            <line
              key={f}
              x1="0"
              x2={L}
              y1={topo + f * (chao - topo)}
              y2={topo + f * (chao - topo)}
              className="db-grade"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          <line
            x1="0"
            x2={L}
            y1={chao}
            y2={chao}
            className="db-chao"
            vectorEffect="non-scaling-stroke"
          />
          {pontos.map((p, i) => (
            <rect
              key={`b${String(i)}`}
              x={cx(i) - larg / 2}
              y={y(p.atual)}
              width={larg}
              height={Math.max(0, chao - y(p.atual))}
              rx="2"
              className={`db-barra${p.futura ? ' db-barra-futura' : ''}${foco === i ? ' db-barra-foco' : ''}`}
            />
          ))}
          {trechos.map((t) => (
            <polyline
              key={t}
              points={t}
              className="db-linha-base"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {pontos.map((p, i) => (
            <rect
              key={`h${String(i)}`}
              x={slot * i}
              y="0"
              width={slot}
              height={A}
              fill="transparent"
              onMouseEnter={() => setFoco(i)}
            >
              <title>
                {`${p.dica ?? p.rotulo}: ${formato(p.atual)}${p.base !== null && rotuloBase ? ` · ${rotuloBase}: ${formato(p.base)}` : ''}`}
              </title>
            </rect>
          ))}
        </svg>
        {pf && foco !== null && (
          <div
            className={`db-dica${foco > pontos.length * 0.62 ? ' db-dica-esq' : ''}`}
            style={{ left: `${String(((foco + 0.5) / n) * 100)}%` }}
            aria-hidden="true"
          >
            <div className="db-dica-titulo">{pf.dica ?? pf.rotulo}</div>
            <div className="db-dica-valor">
              {rotuloAtual}: {formato(pf.atual)}
            </div>
            {pf.base !== null && rotuloBase && (
              <div className="db-dica-base">
                {rotuloBase}: {formato(pf.base)}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="db-eixo" aria-hidden="true">
        {pontos.map((p, i) =>
          i % cadaRotulo === 0 || i === pontos.length - 1 ? (
            <span key={p.rotulo} style={{ left: `${String(((i + 0.5) / n) * 100)}%` }}>
              {p.rotulo}
            </span>
          ) : null,
        )}
      </div>
    </div>
  );
}

/** Legenda "■ Hoje  — Média" do cabeçalho dos gráficos. */
export function LegendaDasBarras({ atual, base }: { atual: string; base: string | null }) {
  return (
    <span className="db-legenda">
      <span>
        <i className="db-legenda-barra" />
        {atual}
      </span>
      {base && (
        <span className="db-legenda-base">
          <i className="db-legenda-linha" />
          {base}
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Sparkline e linha curta
 * ------------------------------------------------------------------ */

export function Sparkline({ serie, tom }: { serie: readonly number[]; tom: 'ruim' | 'normal' }) {
  if (serie.length < 2) return null;
  const max = Math.max(...serie);
  const min = Math.min(...serie);
  const faixa = max - min || 1;
  const pts = serie
    .map(
      (v, i) =>
        `${(1 + (i * 50) / (serie.length - 1)).toFixed(1)},${(16 - ((v - min) / faixa) * 13).toFixed(1)}`,
    )
    .join(' ');
  return (
    <svg
      width="52"
      height="18"
      viewBox="0 0 52 18"
      fill="none"
      className={`db-spark${tom === 'ruim' ? ' db-spark-ruim' : ''}`}
      aria-hidden="true"
    >
      <polyline points={pts} />
    </svg>
  );
}

/** Linha curta com rótulos embaixo (tendência e detalhe do insight). */
export function LinhaCurta({
  serie,
  altura = 70,
  referencia,
}: {
  serie: readonly { readonly rotulo: string; readonly valor: number }[];
  altura?: number;
  referencia?: number | null;
}) {
  if (serie.length < 2) return null;
  const L = 320;
  const vals = serie.map((s) => s.valor);
  const max = Math.max(...vals, referencia ?? -Infinity);
  const min = Math.min(...vals, referencia ?? Infinity);
  const faixa = max - min || 1;
  const y = (v: number) => altura - 8 - ((v - min) / faixa) * (altura - 18);
  const x = (i: number) => 6 + (i * (L - 12)) / (serie.length - 1);
  const pts = serie.map((s, i) => `${x(i).toFixed(1)},${y(s.valor).toFixed(1)}`).join(' ');
  return (
    <div className="db-linha-curta">
      <svg
        viewBox={`0 0 ${String(L)} ${String(altura)}`}
        preserveAspectRatio="none"
        style={{ height: altura }}
        aria-hidden="true"
      >
        <line
          x1="0"
          x2={L}
          y1={altura - 4}
          y2={altura - 4}
          className="db-chao-claro"
          vectorEffect="non-scaling-stroke"
        />
        {referencia !== undefined && referencia !== null && (
          <line
            x1="0"
            x2={L}
            y1={y(referencia)}
            y2={y(referencia)}
            className="db-linha-ref"
            vectorEffect="non-scaling-stroke"
          />
        )}
        <polyline points={pts} className="db-linha-serie" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="db-linha-curta-eixo">
        {serie.map((s) => (
          <span key={s.rotulo}>{s.rotulo}</span>
        ))}
      </div>
    </div>
  );
}

/** Cartão com cabeçalho (título, subtítulo, algo à direita). */
export function CartaoDoPainel({
  titulo,
  sub,
  direita,
  className,
  children,
}: {
  titulo: ReactNode;
  sub?: ReactNode;
  direita?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`db-cartao${className ? ` ${className}` : ''}`}>
      <header className="db-cartao-topo">
        <div className="db-cartao-titulos">
          <h2>{titulo}</h2>
          {sub && <p>{sub}</p>}
        </div>
        {direita}
      </header>
      {children}
    </section>
  );
}
